import React, { useState } from 'react';

import { Button, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { PopoverFilterEntry } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import {
  EventRepository,
  EventTypes,
  ParsedEvent,
  getEventACHType,
  getEventIcon,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';

import { Action, EventDetail, EventIcon, EventMeta, EventType, JSONButton } from './Events';

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

export const PageWebhookEvents: React.FC = () => {
  const navigate = useNavigate();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('webhookEvents');

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    return EventRepository.getAllWebhooks(params).then((response) => {
      const entries = response.events.map(
        (entry): EventsTableRow => ({
          ...parseEvent(entry),
          action: (
            <Action>
              <JSONButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenEventDetail(entry.data);
                }}
              >
                JSON
              </JSONButton>
            </Action>
          ),
        })
      );
      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const eventTypes = Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name));

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: eventTypes.map((eventType) => ({
        label: eventType,
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: DataTableColumnDef<EventsTableRow>[] = [
    {
      header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      cell: ({ value }) => (
        <EventMeta>
          <EventType>
            {getRawEventDirection(value.eventType)} {getRawEventType(value.eventType)} {getEventACHType(value)}
          </EventType>

          <EventDetail>
            <EventIcon>{getEventIcon(value.eventType)}</EventIcon>

            <span>{getEventSummary(value)}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      cell: ({ value }) => getEventStatus(value),
    },
    getTimestampColumn<EventsTableRow>({ accessor: 'createdAt', timeFormat, onTimeFormatChange }),
    {
      header: '',
      width: 'min-content',
      accessor: 'action',
      cell: ({ value }) => value,
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <PaginatedDataTable<EventsTableRow>
        tableId="webhookEvents"
        fetch={handleFetch}
        filter={filter}
        empty={{
          icon: <Icon.AnimationSignal />,
          title: 'No webhook events found',
          description: 'Create your first person or a business entity.',
          action: (
            <Button onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)} size="small" icon={<Icon.User />}>
              Create Entity
            </Button>
          ),
        }}
        columns={columns}
        rowClassName="min-h-11 py-1"
        onRowClick={(row) =>
          navigate(
            getRawEventType(row.eventType) === 'Identity'
              ? `${getEventRoute(row.eventType)}/${(row.data.subType ?? 'undefined').toLowerCase()}/${row.data.id}`
              : getRawEventType(row.eventType) === undefined
                ? ''
                : `${getEventRoute(row.eventType)}/${row.data.id}`
          )
        }
      />
    </>
  );
};
